import { eventBus } from '@/libs/eventBus';
import { WidgetButton } from '../widgetButton';
import { WidgetDivider } from '../widgetDivider';
import { WIDGET_COLORS } from '../../constants/colors';
import {
  formatPoints,
  formatCurrency,
  downPaymentPercent,
  familiesHelped,
} from '../../utils/formatSavingsPoints';
import type { SavingsPointsData } from '../../hooks/useGetSavingsPoints';
import { WidgetCardHeader } from '../widgetCardHeader';
import { ProgressItem } from '../progressItem';
import { StatRow } from '../statRow';
import { WidgetNotice } from '../widgetNotice';
import { PointsBreakdownLegend } from '../pointsBreakdownLegend';
import { RateChip } from '../rateChip';
import { DownPaymentTooltip } from '../downPaymentTooltip';

type Props = { data: SavingsPointsData };

export function DownPaymentNeeded({ data }: Props) {
  const investmentNum = parseFloat(data.pfhpp_investment) || 0;
  const additionalNum = parseFloat(data.additional_downpayment_needed);
  const dpTarget = (investmentNum + additionalNum).toString();
  const dpPercent = downPaymentPercent(investmentNum.toString(), dpTarget);
  const families = familiesHelped(data.savings_points_earned);
  const hasPropertyInfo = !!data.property_value && !!data.deposit_percentage;

  const bonus = parseFloat(data.bonus_credits_points) || 0;
  const referral = parseFloat(data.referral_credits_points) || 0;
  const base = Math.max(0, parseFloat(data.savings_points_earned) - bonus - referral);

  return (
    <>
      <WidgetCardHeader variant="warning" badgeText="Action Required" />

      <WidgetDivider />

      <ProgressItem
        label="Community Points"
        value={
          <span className="font-bold" style={{ color: WIDGET_COLORS.green.text }}>
            ✓ {formatPoints(data.savings_points_earned)} of{' '}
            {formatPoints(data.savings_points_required)} required
          </span>
        }
        barPercent={100}
        segments={[{ flex: 1, color: WIDGET_COLORS.green.bar }]}
        footer={
          <PointsBreakdownLegend
            basePoints={base.toString()}
            bonusPoints={data.bonus_credits_points}
            referralPoints={data.referral_credits_points}
          />
        }
      />

      <ProgressItem
        label="Down Payment (GYS Home balance)"
        value={
          hasPropertyInfo ? (
            <DownPaymentTooltip
              propertyValue={data.property_value!}
              depositPercentage={data.deposit_percentage!}
              targetAmount={dpTarget}
            >
              <span className="text-gray-500">
                <strong className="text-gray-800">{formatCurrency(investmentNum.toString())}</strong> of{' '}
                {formatCurrency(dpTarget)} target
              </span>
            </DownPaymentTooltip>
          ) : (
            <span className="text-gray-500">
              <strong className="text-gray-800">{formatCurrency(investmentNum.toString())}</strong> of{' '}
              {formatCurrency(dpTarget)} target
            </span>
          )
        }
        barPercent={dpPercent}
        segments={[{ flex: 1, color: WIDGET_COLORS.amber.bar }]}
      />

      <StatRow
        bg={WIDGET_COLORS.amber.bg}
        dividerColor={WIDGET_COLORS.amber.divider}
        left={{
          value: `${formatPoints(data.savings_points_earned)} pts`,
          valueColor: WIDGET_COLORS.green.text,
          label: 'Community Points ✓',
          extra: <RateChip totalInvestment={data.total_investment} />,
        }}
        right={{
          value: 'Delayed',
          valueColor: WIDGET_COLORS.amber.text,
          label: 'Waiting for down payment',
        }}
      />

      <WidgetNotice variant="warn" icon="alert-triangle">
        Congratulations on collecting all your community points
        {families > 0 && (
          <>
            {' '} - you have helped <strong>{families} {families === 1 ? 'family' : 'families'}</strong> buy
            their homes on your journey!
          </>
        )}{' '}
        All you have to do now is invest the remainder of your down payment and it will be your turn
        to apply for Pfida&apos;s home finance.
      </WidgetNotice>

      <WidgetDivider />

      <WidgetButton onClick={() => eventBus.emit('mife:open-top-up-modal', {})}>
        Top up your down payment
      </WidgetButton>
    </>
  );
}
